import React, { useState, useMemo } from 'react';
import GaugeChart from 'react-gauge-chart';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Grid,
  Stack,
  Avatar
} from '@mui/material';
import SpeedIcon from '@mui/icons-material/Speed';

export default function GaugeCharts({ selectedEvent, submissions = [], dashboardMetrics }) {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate real conversion data from submissions (per-interest Contracted)
const gaugeData = useMemo(() => {
  // Track totals and contracted counts per interest type
  const interestKeys = ['auto', 'home', 'business', 'life'];
  const byType = Object.fromEntries(
    interestKeys.map(k => [k, { total: 0, contracted: 0 }])
  );

  submissions.forEach(submission => {
    const list = Array.isArray(submission.insuranceTypes) ? submission.insuranceTypes : [];
    list.forEach(insurance => {
      const typeRaw = typeof insurance === 'string' ? insurance : insurance?.type;
      const status = typeof insurance === 'object' ? (insurance?.status || 'New') : 'New';
      const type = (typeRaw || '').toLowerCase();

      if (byType[type]) {
        byType[type].total += 1;
        if (status === 'Contracted') {
          byType[type].contracted += 1;
        }
      }
    });
  });

  const make = (key, label, color) => {
    const t = byType[key];
    const rate = t.total > 0 ? t.contracted / t.total : 0;
    return {
      label: `${label} Conversion`,
      value: rate,           // 0..1 for gauge percent
      color,
      leads: t.total,        // total interests of this type
      conversions: t.contracted, // contracted count (per-interest)
      description: `${label.toLowerCase()} insurance contracted conversion rate`
    };
  };

  return [
    make('auto', 'Auto', '#2196f3'),
    make('home', 'Home', '#ff9800'),
    make('business', 'Business', '#4caf50'),
    make('life', 'Life', '#e91e63')
  ];
}, [submissions]);

  const GaugeCard = ({ gauge, index }) => {
    const [cardHovered, setCardHovered] = useState(false);
    
    const getPerformanceLevel = (value) => {
      if (value >= 0.8) return { level: 'Excellent', color: '#10b981' };
      if (value >= 0.6) return { level: 'Good', color: '#f59e0b' };
      if (value >= 0.4) return { level: 'Average', color: '#3b82f6' };
      if (value >= 0.2) return { level: 'Improving', color: '#8b5cf6' };
      return { level: 'Needs Focus', color: '#ef4444' };
    };

    const performance = getPerformanceLevel(gauge.value);
    const hasData = gauge.leads > 0;

    return (
      <Card 
        sx={{ 
          borderRadius: 2.5, 
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
          border: '1px solid rgba(0,0,0,0.06)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            '& .gauge-container': {
              transform: 'scale(1.02)'
            }
          },
          position: 'relative',
          overflow: 'hidden',
          height: '100%',
          minHeight: { xs: 220, md: 280 },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${gauge.color}, ${gauge.color}aa)`
          }
        }}
        onMouseEnter={() => setCardHovered(true)}
        onMouseLeave={() => setCardHovered(false)}
      >
        <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Compact Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Avatar sx={{
              bgcolor: gauge.color,
              width: 28,
              height: 28,
              boxShadow: `0 2px 6px ${gauge.color}30`,
              transition: 'all 0.3s ease'
            }}>
              <SpeedIcon fontSize="small" sx={{ fontSize: '1rem' }} />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" sx={{ 
                fontWeight: 700, 
                mb: 0.25,
                color: '#1a1a1a',
                fontSize: '0.8rem',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>
                {gauge.label.replace(' Conversion', '')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ 
                fontWeight: 500,
                lineHeight: 1,
                fontSize: '0.6rem'
              }}>
                Conversion rate
              </Typography>
            </Box>
            
            {/* Performance Badge */}
            <Box sx={{
              backgroundColor: hasData ? performance.color : '#9e9e9e',
              color: 'white',
              px: 0.5,
              py: 0.25,
              borderRadius: 0.75,
              fontSize: '0.5rem',
              fontWeight: 600,
              boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
              textAlign: 'center',
              minWidth: 'fit-content'
            }}>
              {hasData ? performance.level : 'No Data'}
            </Box>
          </Box>

          {/* Gauge Chart */}
          <Box className="gauge-container" sx={{ 
            position: 'relative', 
            mb: 1,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'transform 0.3s ease',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 1,
            p: 0.5,
            boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.05)',
            minHeight: 100
          }}>
            {hasData ? (
              <>
                <GaugeChart
                  id={`gauge-${index}`}
                  nrOfLevels={20}
                  percent={gauge.value}
                  colors={[
                    gauge.color + '10',
                    gauge.color + '30',
                    gauge.color + '40',
                    gauge.color + '80',
                    gauge.color
                  ]}
                  arcWidth={0.15}
                  textColor="#111827"
                  needleColor={gauge.color}
                  needleBaseColor={gauge.color}
                  hideText={true}
                  animate={true}
                  animateDuration={1800}
                  animateDelay={index * 150}
                  style={{
                   width: '100%',
                   height: window?.innerWidth && window.innerWidth < 900 ? '80px' : '90px',
                   filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                  }}
                  formatTextValue={value => `${value}%`}
                />
                
                {/* Custom Percentage Display */}
                <Box sx={{
                  position: 'absolute',
                  bottom: -2,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  textAlign: 'center',
                  zIndex: 10
                }}>
                  
                </Box>
              </>
            ) : (
              /* No Data State */
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary'
              }}>
                <Box sx={{
                  width: 60,
                  height: 60,
                  borderRadius: '50%',
                  border: '2px dashed rgba(0,0,0,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 1,
                  fontSize: '1.5rem'
                }}>
                  📊
                </Box>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', textAlign: 'center' }}>
                  No leads yet
                </Typography>
              </Box>
            )}
          </Box>

          {/* Stats */}
          <Box sx={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: 1,
            p: 0.75,
            border: '1px solid rgba(0,0,0,0.06)',
            mt: 'auto'
          }}>
            <Stack direction="row" justifyContent="space-around" alignItems="center" spacing={0.5}>
              <Box textAlign="center" sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ 
                  color: hasData ? gauge.color : '#9e9e9e',
                  lineHeight: 1,
                  mb: 0.25,
                  fontSize: '0.85rem'
                }}>
                  {gauge.conversions}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.2,
                  fontSize: '0.55rem'
                }}>
                  Conv.
                </Typography>
              </Box>
              
              <Box textAlign="center" sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ 
                  color: '#64748b',
                  lineHeight: 1,
                  mb: 0.25,
                  fontSize: '0.85rem'
                }}>
                  {gauge.leads}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.2,
                  fontSize: '0.55rem'
                }}>
                  Leads
                </Typography>
              </Box>
              
              <Box textAlign="center" sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ 
                  color: hasData ? performance.color : '#9e9e9e',
                  lineHeight: 1,
                  mb: 0.25,
                  fontSize: '0.85rem'
                }}>
                  {(gauge.value * 100).toFixed(0)}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.2,
                  fontSize: '0.55rem'
                }}>
                  Rate
                </Typography>
              </Box>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
        <Avatar sx={{
          bgcolor: '#6366f1',
          width: 44,
          height: 44,
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
        }}>
          <SpeedIcon />
        </Avatar>
        <Box>
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1a1a1a', mb: 0.5, fontSize: '1.35rem' }}>
            Conversion Performance
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
            {submissions.length > 0 
              ? `Real-time conversion rates from ${submissions.length} submissions`
              : 'No submissions data available yet'
            }
          </Typography>
        </Box>
      </Box>
      
      {/* Grid for 4 gauges */}
      <Grid container spacing={1.5} sx={{ width: '100%' }}>
        {gaugeData.map((gauge, index) => (
          <Grid 
            item 
            xs={12} 
            sm={6} 
            md={3} 
            lg={3} 
            xl={3} 
            key={gauge.label}
            sx={{ 
              display: 'flex',
              '& .MuiGrid-item': {
                display: 'flex'
              }
            }}
          >
            <GaugeCard gauge={gauge} index={index} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}